/**
 * Primitive-module className helper.
 *
 * `cn(...inputs)` joins the truthy values from a variadic list of class
 * strings (or falsy values). It is the tiny utility every primitive in
 * this module reaches for when it needs to combine base classes with
 * variant/size classes and a caller-supplied `className` override.
 *
 * Shape is a deliberate subset of `clsx`: accepts `string | false | null
 * | undefined`, drops the falsy entries, trims extra whitespace, and
 * returns a single space-separated string. `clsx` / `tailwind-merge` are
 * NOT installed in this repo and MUST NOT be added without governance
 * authorisation (D-043 dependency-baseline posture); this 12-line
 * hand-rolled helper avoids the dependency and stays readable.
 *
 * Tailwind class-order conflicts (e.g. `px-3 px-4` from a base + override)
 * resolve left-to-right in the CSS cascade the same way `clsx` output
 * does: later wins. Primitives here intentionally expose the caller's
 * `className` last so consumers can override individual utility classes
 * without re-authoring the full string.
 */

export type ClassValue = string | false | null | undefined;

export function cn(...inputs: readonly ClassValue[]): string {
  return inputs
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .join(' ')
    .trim();
}
